import React from "react"
import { Link, useLocation } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { cn } from "../lib/utils"
import { useSidebar } from "../hooks/use-layout"
import { Button } from "../components/ui/button"
import {
 DropdownMenu,
 DropdownMenuContent,
 DropdownMenuItem,
 DropdownMenuLabel,
 DropdownMenuSeparator,
 DropdownMenuTrigger,
} from "../components/ui/dropdown-menu"
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar"
import { Separator } from "../components/ui/separator"
import {
 Menu,
 Maximize,
 Minimize,
 Moon,
 Sun,
 ChevronDown,
 LogOut,
 User,
 Settings,
 Home,
 Store,
 Trash2,
 RefreshCw,
 AlertTriangle,
} from "lucide-react"
import { useAuth } from "slices/user/hooks"
import BranchSwitcher from "Components/Common/BranchSwitcher"
import LanguageSwitcher from "Components/Common/LanguageSwitcher"
import NotificationDropdown from "Components/Common/NotificationDropdown"
import { useOnlineStatus } from "hooks/useOnlineStatus"
import { useOfflineBootstrapStatus } from "hooks/useOfflineBootstrapStatus"
import FailedOfflineOrdersDialog from "Components/Common/FailedOfflineOrdersDialog"
import FailedTableOpsDialog from "Components/Common/FailedTableOpsDialog"
import FailedMutationsDialog from "Components/Common/FailedMutationsDialog"
import { DeleteConfirmDialog } from "../components/delete-confirm-dialog"
import { resolveActiveNavLink } from "helpers/navActiveLink"
import { ConnectionStatusChip } from "../components/connection-status-chip"
import { useAutoUpdater } from "hooks/useAutoUpdater"
import { formatDateTimeShort } from "helpers/dateTime"
import { Loader2 } from "lucide-react"
interface HeaderProps {
 onToggleTheme?: () => void
 isDark?: boolean
}

export function Header({ onToggleTheme, isDark = false }: HeaderProps) {
 const { t, i18n } = useTranslation()
 const quickNavItems = React.useMemo(
 () => [
 { path:"/", label: t("Home"), icon: Home },
 { path:"/pos", label: t("POS title"), icon: Store },
 ],
 [t]
 )
 const { toggle, toggleMobile } = useSidebar()
 const location = useLocation()
 const [isFullscreen, setIsFullscreen] = React.useState(false)
 const { isOnline } = useOnlineStatus()
 const {
 status: offlineBootstrap,
 syncing: offlineSyncing,
 syncNow,
 summary: offlineSummary,
 refreshOfflineBootstrapStatus,
 } = useOfflineBootstrapStatus()

 const [failedOrdersOpen, setFailedOrdersOpen] = React.useState(false)
 const [failedTableOpsOpen, setFailedTableOpsOpen] = React.useState(false)
 const [failedMutationsOpen, setFailedMutationsOpen] = React.useState(false)

 const quickNavPaths = React.useMemo(
 () => quickNavItems.map((item) => item.path),
 [quickNavItems],
 )
 const activeQuickNavPath = React.useMemo(
 () => resolveActiveNavLink(quickNavPaths, location.pathname, location.search),
 [quickNavPaths, location.pathname, location.search],
 )
 const isActive = (path: string) => activeQuickNavPath === path

 const toggleFullscreen = () => {
 if (!document.fullscreenElement) {
 document.documentElement.requestFullscreen()
 setIsFullscreen(true)
 } else {
 document.exitFullscreen()
 setIsFullscreen(false)
 }
 }

 const { user, hasPermission, isSuperAdmin } = useAuth()
 const role = user?.roles?.[0] ?? user?.role ?? ''
 const isCashier = role === 'Cashier'|| role === 'cashier'
 const showTenantSettings = isSuperAdmin || hasPermission('manage_settings')
 const userName = user?.name ?? t('header.defaultUser')
 const userAvatar = user?.avatar
 const [showClearCache, setShowClearCache] = React.useState(false)
 const [clearingCache, setClearingCache] = React.useState(false)

 const isElectron = typeof window !== 'undefined' && !!(window as any).electronAPI
 const { checkForUpdates, status } = useAutoUpdater()
 const isChecking = status === 'checking'
 const offlineAttentionCount = offlineSummary.attentionCount
 const otherOpsAttention =
  (offlineSummary.failedMutations ?? 0) + (offlineSummary.mutationConflicts ?? 0)
 const syncDetailTitle = [
 offlineSummary.pendingForSync > 0
 ? t('header.syncPending', { count: offlineSummary.pendingForSync })
 : '',
 offlineSummary.failedOrders > 0
 ? t('header.syncFailed', { count: offlineSummary.failedOrders })
 : '',
 (offlineSummary.failedMutations ?? 0) > 0
 ? t('header.syncFailedOther', { count: offlineSummary.failedMutations })
 : '',
 offlineSummary.conflictCount > 0
 ? t('header.syncConflict', { count: offlineSummary.conflictCount })
 : '',
 ]
 .filter(Boolean)
 .join('\n')
 const lastOfflineSync = offlineBootstrap?.last_success_at
 ? formatDateTimeShort(offlineBootstrap.last_success_at)
 : t('header.syncNotCompletedYet')

 const handleClearCache = () => {
 setClearingCache(true)
 localStorage.clear()

 const reload = () => { window.location.reload() }

 const clearCachesAndReload = async () => {
 if ('caches' in window) {
 const cacheNames = await (window as any).caches.keys()
 await Promise.all(cacheNames.map((n: string) => (window as any).caches.delete(n)))
 }
 reload()
 }

 if (indexedDB.databases) {
 indexedDB.databases().then((dbs) => {
 dbs.forEach((db) => {
 if (db.name) indexedDB.deleteDatabase(db.name)
 })
 }).finally(clearCachesAndReload)
 } else {
 clearCachesAndReload()
 }
 }

 return (
 <header
 className={cn(
"app-header sticky top-[var(--app-banner-h,0px)] z-30 flex h-14 min-w-0 max-w-full items-center justify-between gap-3 border-b border-border-subtle bg-surface-header transition-[top] duration-200",
 /* Align with AppShell .page-wrapper — sidebar offset is on .app-content only */
"px-3 sm:px-4 lg:px-6")}
 >
 {/* RTL start (يمين): sidebar toggle + quick navigation */}
 <div className="flex min-h-0 min-w-0 items-center gap-2">
 <Button
 variant="ghost" size="icon-sm"
onClick={toggleMobile}
 className="shrink-0 md:hidden">
 <Menu className="h-5 w-5"/>
 </Button>

 <Button
 variant="ghost" size="icon-sm"
onClick={toggle}
 className="hidden shrink-0 md:inline-flex">
 <Menu className="h-5 w-5"/>
 </Button>

 <nav
 className="hidden min-w-0 items-center gap-1.5 overflow-x-auto [scrollbar-width:thin] md:flex" dir={i18n.dir()}
 >
 {quickNavItems
 .filter((item) => {
 if (isCashier && item.path !== "/pos") return false
 return true
 })
 .map((item) => {
 const active = isActive(item.path)
 return (
 <Link
 key={item.path}
 to={item.path}
 className={cn(
"flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-sm font-medium transition-colors",
 active
 ? "bg-primary-soft-strong/90 text-primary-soft-foreground font-semibold ring-1 ring-inset ring-primary-soft-border/60 dark:bg-[hsl(var(--brand-accent)_/_0.14)] dark:text-foreground dark:ring-[hsl(var(--brand-accent)_/_0.35)]":"text-muted-foreground hover:bg-muted hover:text-foreground")}
 >
 <item.icon
 className={cn(
"h-4 w-4 shrink-0",
 active ? "text-primary-soft-foreground dark:text-[hsl(var(--brand-accent))]":"text-icon")}
 />
 <span className="whitespace-nowrap">{item.label}</span>
 </Link>
 )
 })}
 </nav>
 </div>

 {/* RTL end (شمال): branch, status, utilities, profile */}
 <div className="flex min-w-0 shrink items-center gap-1.5 sm:gap-2">
            {/* Mobile: icon-only BranchSwitcher must not shrink into the status chip */}
            <div className="shrink-0 sm:min-w-0 sm:max-w-[280px] sm:shrink">
              <BranchSwitcher />
            </div>

 <div className="flex shrink-0 items-center">
 <div className="flex items-center overflow-hidden rounded-xl border border-border-subtle bg-card shadow-sm dark:bg-surface-elevated dark:shadow-card">
 <ConnectionStatusChip
 online={isOnline}
 labelWhenOnline={t('Online')}
 labelWhenOffline={t('Offline')}
 showLabelFrom="sm"
 className="h-8 min-w-[2.75rem] rounded-none px-2.5 sm:min-w-[4.5rem] sm:px-3"
 />
 <Button
 type="button" variant="ghost" size="sm" disabled={!isOnline || offlineSyncing}
 onClick={() => { void syncNow() }}
 className={cn(
 'h-8 shrink-0 gap-1.5 rounded-none border-s border-border-subtle px-2.5 text-[11px] font-semibold hover:bg-muted/80',
 offlineAttentionCount > 0 && 'text-warning hover:bg-warning/10 hover:text-warning',
 )}
 title={`${t('header.syncNowTitle', { lastSync: lastOfflineSync })}${syncDetailTitle ? `\n${syncDetailTitle}` : ''}`}
 >
 {offlineSyncing ? (
 <RefreshCw className="h-3.5 w-3.5 shrink-0 animate-spin"/>
 ) : offlineSummary.failedOrders > 0 || offlineSummary.conflictCount > 0 ? (
 <AlertTriangle className="h-3.5 w-3.5 shrink-0"/>
 ) : (
 <RefreshCw className="h-3.5 w-3.5 shrink-0"/>
 )}
 <span className="hidden whitespace-nowrap md:inline">{t('header.syncNow')}</span>
 {offlineAttentionCount > 0 && (
 <span className="rounded-full border border-semantic-warning-border bg-semantic-warning-soft px-1.5 py-0.5 text-[10px] font-bold tabular-nums text-semantic-warning">
 {offlineAttentionCount}
 </span>
 )}
 </Button>
 <DropdownMenu>
 <DropdownMenuTrigger asChild>
 <Button
 type="button" variant="ghost" size="sm" className="h-8 shrink-0 rounded-none border-s border-border-subtle px-2 hover:bg-muted/80" title={t('header.syncMenuTitle')} aria-label={t('header.syncMenuAriaLabel')}>
 <ChevronDown className="h-3.5 w-3.5"/>
 </Button>
 </DropdownMenuTrigger>
 <DropdownMenuContent align="end" dir={i18n.dir()} className="min-w-[14rem]">
 <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
 {t('header.offlineBootstrapStatus')}
 </DropdownMenuLabel>
 <DropdownMenuItem
 className="cursor-pointer"
onClick={() => { void syncNow() }}
 disabled={!isOnline || offlineSyncing}
 >
 <RefreshCw className="h-3.5 w-3.5 shrink-0"/>
 {t('header.syncNow')}
 </DropdownMenuItem>
 <DropdownMenuSeparator />
 <DropdownMenuItem
 className="cursor-pointer" disabled={offlineSummary.failedPosOrders === 0}
 onClick={() => setFailedOrdersOpen(true)}
 >
 <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-warning"/>
 {t('header.failedPosOrders')}
 {offlineSummary.failedPosOrders > 0 && (
 <span className="me-auto text-[10px] font-bold text-warning">
 {offlineSummary.failedPosOrders}
 </span>
 )}
 </DropdownMenuItem>
 <DropdownMenuItem
 className="cursor-pointer" disabled={offlineSummary.failedTableOps === 0}
 onClick={() => setFailedTableOpsOpen(true)}
 >
 <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-warning"/>
 {t('header.failedTableOps')}
 {offlineSummary.failedTableOps > 0 && (
 <span className="me-auto text-[10px] font-bold text-warning">
 {offlineSummary.failedTableOps}
 </span>
 )}
 </DropdownMenuItem>
 <DropdownMenuItem
 className="cursor-pointer" disabled={otherOpsAttention === 0}
 onClick={() => setFailedMutationsOpen(true)}
 >
 <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-warning"/>
 {t('header.failedOtherOps')}
 {otherOpsAttention > 0 && (
 <span className="me-auto text-[10px] font-bold text-warning">
 {otherOpsAttention}
 </span>
 )}
 </DropdownMenuItem>
 </DropdownMenuContent>
 </DropdownMenu>
 </div>
 </div>

 <Separator orientation="vertical" className="mx-0.5 hidden h-5 shrink-0 opacity-60 sm:block"/>

 <div className="flex shrink-0 items-center gap-0.5 sm:gap-1">
 <NotificationDropdown />

 <LanguageSwitcher />

 <Button
 variant="ghost" size="icon-sm"
onClick={onToggleTheme}
 className="shrink-0">
 {isDark ? <Sun className="h-4 w-4"/> : <Moon className="h-4 w-4"/>}
 </Button>

 <Button
 variant="ghost" size="icon-sm"
onClick={toggleFullscreen}
 className="hidden shrink-0 sm:flex">
 {isFullscreen ? (
 <Minimize className="h-4 w-4"/>
 ) : (
 <Maximize className="h-4 w-4"/>
 )}
 </Button>
 </div>

 <Separator orientation="vertical" className="mx-0.5 hidden h-5 shrink-0 opacity-60 sm:block"/>

 <DropdownMenu>
 <DropdownMenuTrigger asChild>
 <Button
 variant="ghost" className="flex h-9 shrink-0 items-center gap-2 rounded-xl px-2">
 <Avatar className="h-7 w-7">
 <AvatarImage src={userAvatar} />
 <AvatarFallback className="bg-primary text-white text-[10px] font-bold">
 {userName?.charAt(0) || "U"}
 </AvatarFallback>
 </Avatar>
 <span className="hidden max-w-[120px] truncate sm:block text-sm font-medium">
 {userName}
 </span>
 <ChevronDown className="h-3 w-3 shrink-0 text-icon"/>
 </Button>
 </DropdownMenuTrigger>
 <DropdownMenuContent
 align="end" dir={i18n.dir()} className="w-56 bg-popover text-popover-foreground border-border">
 <DropdownMenuLabel className="font-normal">
 <div className="flex flex-col space-y-1 text-start">
 <p className="text-sm font-medium leading-none text-foreground">{userName}</p>
 <p className="text-xs leading-none text-muted-foreground capitalize">
 {role || "user"}
 </p>
 </div>
 </DropdownMenuLabel>
 <DropdownMenuSeparator />
 <DropdownMenuItem asChild>
 <Link to="/profile" className="flex w-full items-center gap-2 cursor-pointer">
 <User className="h-4 w-4 shrink-0 text-muted-foreground"/>
 <span>{t('header.profile')}</span>
 </Link>
 </DropdownMenuItem>
 {showTenantSettings ? (
 <DropdownMenuItem asChild>
 <Link to="/settings" className="flex w-full items-center gap-2 cursor-pointer">
 <Settings className="h-4 w-4 shrink-0 text-muted-foreground"/>
 <span>{t('header.tenantSettings')}</span>
 </Link>
 </DropdownMenuItem>
 ) : null}
 {isElectron && (
 <DropdownMenuItem
 onClick={checkForUpdates}
 disabled={isChecking}
 className="flex items-center gap-2 cursor-pointer"
 >
 {isChecking ? (
 <Loader2 className="h-4 w-4 shrink-0 animate-spin text-muted-foreground"/>
 ) : (
 <RefreshCw className="h-4 w-4 shrink-0 text-muted-foreground"/>
 )}
 <span>{t('header.checkForUpdates')}</span>
 </DropdownMenuItem>
 )}
 <DropdownMenuSeparator />
 <DropdownMenuItem
 onClick={() => setShowClearCache(true)}
 className="flex items-center gap-2 cursor-pointer text-warning focus:bg-warning/10 focus:text-warning">
 <Trash2 className="h-4 w-4 shrink-0"/>
 <span>{t('header.clearCache')}</span>
 </DropdownMenuItem>
 <DropdownMenuSeparator />
 <DropdownMenuItem asChild variant="destructive">
 <Link
 to="/logout" className="flex w-full items-center gap-2 cursor-pointer">
 <LogOut className="h-4 w-4 shrink-0 -scale-x-100"/>
 <span>{t('header.logout')}</span>
 </Link>
 </DropdownMenuItem>
 </DropdownMenuContent>
 </DropdownMenu>
 </div>

 <DeleteConfirmDialog
 open={showClearCache}
 onOpenChange={setShowClearCache}
 onConfirm={handleClearCache}
 title={t('header.clearCache')} description={t('header.clearCacheDescription')} confirmText={t('header.clearCache')} cancelText={t('header.cancel')} loading={clearingCache}
 />

 <FailedOfflineOrdersDialog
 open={failedOrdersOpen}
 onOpenChange={setFailedOrdersOpen}
 onCountsMayHaveChanged={refreshOfflineBootstrapStatus}
 />
 <FailedTableOpsDialog
 open={failedTableOpsOpen}
 onOpenChange={setFailedTableOpsOpen}
 onCountsMayHaveChanged={refreshOfflineBootstrapStatus}
 />
 <FailedMutationsDialog
 open={failedMutationsOpen}
 onOpenChange={setFailedMutationsOpen}
 onCountsMayHaveChanged={refreshOfflineBootstrapStatus}
 />
 </header>
 )
}
